import React from "react";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { AsVo } from "src/vo/asVo";
import { getCookie } from "src/utils/cookieUtil";
import { VisitVo } from "src/vo/visitVo";
import { baseQueryWithIntercept } from "src/utils/rtkQueryBaseQueryUtil";

const serverIp = process.env.REACT_APP_SERVER_IP;

export type homeState = {
  readonly title: string;
  readonly count: number;
};

// 1-1. initial state 객체 생성
const initialState: AsVo = {
  progress: 0,
};

export const asAPI = createApi({
  baseQuery: baseQueryWithIntercept,
  tagTypes: ["AsVo"],
  refetchOnMountOrArgChange: true,
  // keepUnusedDataFor: 0,
  reducerPath: "asAPI",
  endpoints: (builder) => ({
    //  ========================= as 상세 화면 =========================
    asDetail: builder.query({
      query: (index) => ({
        url: `/as/${index}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
        providesTags: ["AsVo"],
      }),
      transformResponse: (response: any) => response.data,
      // transformResponse: (response: { data: AsVo }, meta, agr) => response.data,
    }),
    // ========================= as 목록 =========================
    asList: builder.query({
      query: (search) => ({
        url: `/as/search`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
        params: search,
      }),
      // transformResponse: (response: any) => response.data,
      // providesTags: (result, error, id) => [{ type: "Post", id }],
      // transformResponse: (response: { data: BaseVo }, meta, agr) => response.data,
    }),
    // ========================= as 등록 =========================
    addAs: builder.mutation({
      query: (data: any) => ({
        url: `as`,
        method: "POST",
        headers: {},
        body: data,
      }),
    }),
    // ========================= as 수정 =========================
    updateAs: builder.mutation({
      query: ({ as_idx, data }) => ({
        url: `as/${as_idx}`,
        method: "PUT",
        headers: {},
        body: data,
      }),
    }),
    // ========================= AS 진행  =========================
    updateAsStart: builder.mutation<void, Pick<AsVo, "as_idx">>({
      query: ({ as_idx }) => ({
        url: `as/${as_idx}/stat/continue`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
      }),
      invalidatesTags: ["AsVo"],
    }),
    // ========================= AS 업체 배정  =========================
    updateAsAsignCompany: builder.mutation<void, Pick<AsVo, "as_idx"> & Partial<AsVo>>({
      query: ({ as_idx, ...data }) => ({
        url: `as/${as_idx}/stat/asign-company`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
        body: data,
      }),
    }),
    // ========================= as 취소 =========================
    cancelAs: builder.mutation<void, number>({
      query: (as_idx) => ({
        url: `as/${as_idx}/cancel`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
      }),
    }),
    // ========================= as 진행완료 =========================
    completeAs: builder.mutation<void, number>({
      query: (as_idx) => ({
        url: `as/${as_idx}/complete`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
      }),
    }),
    //
    deleteAsToDb: builder.mutation<void, number>({
      query: (as_idx) => ({
        url: `as/${as_idx}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
      }),
    }),
    // ========================= as 댓글 등록 =========================
    addAsComment: builder.mutation<void, Pick<AsVo, "as_idx"> & Partial<AsVo>>({
      query: ({ as_idx, ...data }) => ({
        url: `/as/${as_idx}/comment`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
        body: data,
      }),
    }),
    // ========================= 방문자 조회 =========================
    selectVisitInfo: builder.query({
      query: (as_idx) => ({
        url: `/as/${as_idx}/visit-info`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
      }),
      transformResponse: (response: any) => response.data,
    }),
    // ========================= 방문자 등록 =========================
    addVisit: builder.mutation<void, Pick<VisitVo, "as_idx"> & Partial<VisitVo>>({
      query: ({ as_idx, ...data }) => ({
        url: `/as/${as_idx}/visit-info`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
        body: data,
      }),
    }),
    updateVisit: builder.mutation<void, Pick<VisitVo, "as_idx"> & Partial<VisitVo>>({
      query: ({ as_idx, ...data }) => ({
        url: `/as/${as_idx}/visit-info`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
        body: data,
      }),
    }),
    // ========================= store_id로 as 조회 =========================
    selectStoreList: builder.query({
      query: (store_id) => ({
        url: `/as/store/as/${store_id}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*, text/plain",
        },
      }),
    }),
  }),
});

// Export hooks for usage in functional components
// export const { useAsTestQuery } = asListAPI;

export const {
  useAsDetailQuery,
  useAsListQuery,
  useAddAsMutation,
  useUpdateAsMutation,
  useUpdateAsStartMutation,
  useUpdateAsAsignCompanyMutation,
  useCancelAsMutation,
  useCompleteAsMutation,
  useDeleteAsToDbMutation,
  useAddAsCommentMutation,
  useSelectVisitInfoQuery,
  useAddVisitMutation,
  useUpdateVisitMutation,
  useSelectStoreListQuery,
} = asAPI;
